import { useMemo, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useQuery } from "@apollo/client";

import useWindowVisibility from "hooks/useWindowVisibility";
import ComboBoxMultiple from "components/ComboBoxMultiple";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import { getDocsUrl } from "utils/getDocsUrl";
import FormField from "ds/components/Form/Field";
import useComboBoxInputValueItem from "ds/components/ComboBox/useInputValueItem";

import ManagedUserGroupComboBoxItem from "./ComboBoxItem";
import { GET_API_KEY_GROUPS_COMBOBOX } from "./gql";
import { ManagedUserGroupCollection } from "./types";

const noop = () => void 0;

const INPUT_VALUE_ITEM_PROPS = {
  label: "Can't find? Create new group mapping.",
};

const EMPTY_OPTION_PROPS = {
  label: "No options available. Create new group mapping.",
};

type FormFieldManagedUserGroupsProps = {
  name: string;
  label: string;
  isOptional?: boolean;
};

const FormFieldManagedUserGroups = ({
  name,
  label,
  isOptional,
}: FormFieldManagedUserGroupsProps) => {
  const { data, loading, refetch } = useQuery(GET_API_KEY_GROUPS_COMBOBOX);
  const { control, getValues, setValue, watch, formState } = useFormContext<{
    [key: string]: { value: string }[];
  }>();
  const isWindowVisible = useWindowVisibility();

  const fieldValue = watch(name);
  const selectedValues = useMemo(() => fieldValue?.map((item) => item.value) || [], [fieldValue]);

  const existingTeams = useMemo(() => {
    return (
      formState.defaultValues?.[name]
        ?.map((item) => item?.value)
        .filter((value) => value !== undefined) || []
    );
  }, [formState.defaultValues, name]);

  const options = useMemo(() => {
    const idpGroups = data?.managedUserGroups?.map((group) => group.groupName) || [];

    // Create options for all IdP groups
    const idpOptions = idpGroups.map((groupName) => ({
      value: groupName,
      label: groupName,
    }));

    // Add only existing teams that are NOT in IdP groups
    const existingTeamOptions = existingTeams
      .filter((teamName) => !idpGroups.includes(teamName))
      .map((teamName) => ({
        value: teamName,
        label: teamName,
        isUnmapped: true,
      }));

    // Put IdP groups first, then unmapped existing teams at the bottom
    return [...idpOptions, ...existingTeamOptions];
  }, [data?.managedUserGroups, existingTeams]);

  const { comboBoxProps } = useComboBoxInputValueItem({
    options,
    inputValueItemProps: INPUT_VALUE_ITEM_PROPS,
    onInputValueSelected: noop,
    emptyOptionProps: EMPTY_OPTION_PROPS,
  });

  // Clean up form state when available groups change - remove any selected groups that no longer exist
  useEffect(() => {
    if (!options.length) return;

    const currentValues = getValues(name) || [];

    const validValues = currentValues.filter((item) =>
      options.some((option) => option.value === item.value)
    );

    // Only update if some groups were removed
    if (validValues.length !== currentValues.length) {
      setValue(name, validValues);
    }
  }, [name, setValue, getValues, options]);

  // Refetch immediately when window becomes visible (override default 10s debounce)
  useEffect(() => {
    if (isWindowVisible) {
      refetch();
    }
  }, [isWindowVisible, refetch]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormField
          label={label}
          error={fieldState.error?.message}
          isOptional={isOptional}
          tooltipInfo={
            <>
              <TooltipModalTitle>IdP groups</TooltipModalTitle>
              <TooltipModalBody align="start">
                Assign this key to an IdP group mapping by selecting one of the available groups.
                This enables group-based access control.
                {/* TODO: update the link to the new docs when they are ready */}
                <ReadMoreDocsLink
                  docsUrl={getDocsUrl("/concepts/user-management/user.html#groups")}
                />
              </TooltipModalBody>
            </>
          }
          tooltipInfoVariant="modal"
          tooltipMode="parent"
          helperText="Select an existing IdP group mapping to assign this key as part of group-based access control."
        >
          <ComboBoxMultiple<ManagedUserGroupCollection, string>
            id={name}
            isLoading={loading}
            values={selectedValues}
            {...comboBoxProps}
            onChange={comboBoxProps.onChange((groupName) => {
              if (groupName) {
                const currentValues = field.value?.map((item) => item.value) || [];
                if (currentValues.includes(groupName)) {
                  // Remove the item
                  field.onChange(field.value.filter((item) => item.value !== groupName));
                } else {
                  // Add the item
                  field.onChange([...(field.value || []), { value: groupName }]);
                }
              }
            })}
          >
            {(item) => (
              <ManagedUserGroupComboBoxItem
                key={item.value}
                id={item.value}
                label={item.label}
                isSelected={selectedValues.includes(item.value)}
                isInputValueItem={item.isInputValueItem}
                isEmptyValue={item.isEmptyValue}
                isUnmapped={item.isUnmapped}
              />
            )}
          </ComboBoxMultiple>
        </FormField>
      )}
    />
  );
};

export default FormFieldManagedUserGroups;
